import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { money } from "../../lib/helpers";
import { Field } from "../common";
import { ReferenceUpload } from "./ReferenceUpload";

export const EST_TEXT = ["service", "project_description", "scope_of_work", "preparation", "materials", "duration", "warranty", "exclusions", "notes"];
const LABELS = { service: ["Project title", "Título"], project_description: ["Description", "Descripción"], scope_of_work: ["Scope of work", "Alcance"], preparation: ["Preparation", "Preparación"], materials: ["Materials", "Materiales"], duration: ["Duration", "Duración"], warranty: ["Warranty", "Garantía"], exclusions: ["Exclusions", "Exclusiones"], notes: ["Notes", "Notas"] };

function PriceSuggestion({ s, basedOn, onPrice }) {
  const { t } = useLang();
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 grid gap-2" data-testid="ai-price-suggestion">
      <div className="font-bold">{t("Suggested price", "Precio sugerido")}: <span className="mono">{money(s.amount)}</span>{s.low > 0 && <span className="text-xs text-slate-600"> ({money(s.low)} – {money(s.high)})</span>}</div>
      {s.reason && <p className="text-xs text-slate-700">{s.reason}</p>}
      {basedOn?.length > 0 && <p className="text-xs text-slate-500">{t("Based on estimates", "Basado en presupuestos")}: {basedOn.map((b) => `#${b.number}`).join(", ")}</p>}
      <button className="btn-primary btn-sm" onClick={() => onPrice(s.amount)} data-testid="ai-price-approve-btn">{t("Approve and use this price", "Aprobar y usar este precio")}</button>
    </div>
  );
}

export function AIDraftDialog({ f, onApply, onPrice, onClose }) {
  const { t, lang } = useLang();
  const [desc, setDesc] = useState(f.project_description || "");
  const [refs, setRefs] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/estimate-draft", { customer_id: f.customer_id || "", description: desc, language: f.language, reference_ids: refs.map((r) => r.id), current: Object.fromEntries(EST_TEXT.map((k) => [k, f[k] || ""])) });
      setRes(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-draft-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D9531E]" />{t("Write with AI", "Escribir con IA")}</DialogTitle></DialogHeader>
        <Field label={t("Describe the job (what, where, size, condition)", "Describa el trabajo (qué, dónde, tamaño, condición)")}>
          <textarea className="inp min-h-[110px]" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="ai-draft-description" />
        </Field>
        <ReferenceUpload refs={refs} setRefs={setRefs} testid="ai-draft-ref" />
        <button className="btn-primary" disabled={busy || desc.trim().length < 3} onClick={gen} data-testid="ai-draft-generate-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {res ? t("Write again", "Escribir de nuevo") : t("Write estimate", "Escribir presupuesto")}
        </button>
        {res && (
          <div className="grid gap-2" data-testid="ai-draft-result">
            {EST_TEXT.filter((k) => res.fields[k]).map((k) => (
              <div key={k} className="rounded-xl bg-slate-50 p-3"><div className="text-xs font-bold uppercase text-slate-500">{LABELS[k][lang === "es" ? 1 : 0]}</div><div className="text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">{res.fields[k]}</div></div>
            ))}
            {res.price_suggestion && <PriceSuggestion s={res.price_suggestion} basedOn={res.based_on} onPrice={onPrice} />}
            <button className="btn-dark" onClick={() => { onApply(res.fields); onClose(); }} data-testid="ai-draft-apply-btn">{t("Apply texts to the estimate", "Aplicar textos al presupuesto")}</button>
            <p className="text-xs text-slate-500">{t("Nothing is saved or sent until you press Save / Send.", "No se guarda ni se envía nada hasta que usted presione Guardar / Enviar.")}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
